import React from "react";
import { FaGraduationCap } from "@react-icons/all-files/fa/FaGraduationCap";
import { FaLinkedin } from "@react-icons/all-files/fa/FaLinkedin";
import { FaEnvelope } from "@react-icons/all-files/fa/FaEnvelope";
import { FaBuilding } from "@react-icons/all-files/fa/FaBuilding";
import dummyPic from "../../assets/images/faculty/dummy.jpg";

// Helper role classifier
export const classifyStaffRole = (role = "") => {
  const r = (role || "").toLowerCase().trim();

  if (
    r === "group director - aa" ||
    r === "director" ||
    r === "dy. director - aa" ||
    r === "group director" ||
    r === "dy. director"
  ) {
    return "executive";
  }

  if (r.includes("dean")) {
    return "dean";
  }

  if (
    r.includes("asst. director") ||
    r.includes("assistant director") ||
    r.includes("dy.director") ||
    r.includes("dy. director t & p") ||
    r.includes("dy.director t & p")
  ) {
    return "asst_director";
  }

  if (
    r.includes("hod") ||
    r.includes("head of department") ||
    r.includes("(hod)")
  ) {
    return "hod";
  }

  if (
    r.includes("lab") ||
    r.includes("clerk") ||
    r.includes("attendant") ||
    r.includes("account") ||
    r.includes("library") ||
    r.includes("librarian") ||
    r.includes("superintendent") ||
    r.includes("office") ||
    r.includes("steno") ||
    r.includes("store") ||
    r.includes("driver") ||
    r.includes("peon") ||
    r.includes("helper")
  ) {
    return "staff";
  }

  return "faculty";
};

export const isRealPhoto = (pic) => {
  if (!pic) return false;
  if (typeof pic === "string") {
    const p = pic.toLowerCase();
    if (p.includes("dummy") || p.includes("dot-pattern")) return false;
    return p.length > 0;
  }
  return false;
};

// VIP Executive Card component for the Top 3 Leaders
export const ExecutiveLeaderCard = (props) => {
  const { Name, Role, ProfilePic, Letter, onViewProfile } = props;
  const cleanName = (Name || "")
    .replace(/^(Dr\.|Mr\.|Ms\.|Prof\.|S\.)\s*/i, "")
    .trim();
  const initial = Letter || (cleanName ? cleanName[0] : "E");
  const hasPhoto = isRealPhoto(ProfilePic);

  return (
    <div className="relative bg-gradient-to-br from-[#081C3A] via-[#0d2a52] to-[#12396b] text-white rounded-3xl p-6 sm:p-7 shadow-2xl border border-amber-400/40 overflow-hidden group hover:border-amber-400 hover:shadow-amber-500/20 transition-all duration-300 flex flex-col justify-between items-center text-center">
      {/* Background Decorative Glow */}
      <div className="absolute -top-12 -right-12 w-36 h-36 bg-amber-400/15 rounded-full blur-2xl group-hover:bg-amber-400/25 transition-all" />
      <div className="absolute -bottom-12 -left-12 w-36 h-36 bg-blue-500/15 rounded-full blur-2xl" />

      {/* VIP Badge */}
      <div className="mb-4 z-10">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest px-3.5 py-1 rounded-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-slate-950 shadow-lg">
          👑 LKCTC
        </span>
      </div>

      {/* Avatar with Gold Ring Frame */}
      <div className="relative z-10 w-32 h-32 sm:w-36 sm:h-36 rounded-full p-1 bg-gradient-to-tr from-amber-400 via-amber-200 to-amber-500 shadow-2xl group-hover:scale-105 transition-transform duration-300 mb-5">
        <div className="w-full h-full rounded-full overflow-hidden bg-[#081C3A] flex items-center justify-center">
          {hasPhoto ? (
            <img
              src={ProfilePic}
              alt={Name}
              className="w-full h-full object-cover object-top rounded-full"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <span className="text-amber-300 text-4xl font-bold font-serif uppercase">
              {initial}
            </span>
          )}
        </div>
      </div>

      {/* Executive Name & Role */}
      <div className="z-10 w-full">
        <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight leading-snug group-hover:text-amber-300 transition-colors">
          {Name}
        </h3>

        <div className="mt-3">
          <span className="inline-block text-xs sm:text-sm font-bold px-4 py-1.5 rounded-xl bg-white/10 text-amber-300 border border-amber-400/30 backdrop-blur-md shadow-inner">
            {Role}
          </span>
        </div>

        <p className="mt-3 text-xs text-blue-200/80 font-medium">
          Lyallpur Khalsa College Technical Campus
        </p>

        {onViewProfile && (
          <button
            onClick={() => onViewProfile(props)}
            className="mt-4 px-4 py-1.5 rounded-full bg-white/10 hover:bg-amber-400 hover:text-slate-950 text-amber-300 text-xs font-bold transition-all duration-200 border border-amber-400/30 shadow-md"
          >
            Know More &rarr;
          </button>
        )}
      </div>
    </div>
  );
};

// General Faculty Card for Deans, Assistant Directors, HODs, Teaching Staff & Non-Teaching Support Staff
const FacultyCard = (props) => {
  const {
    newClass,
    Role,
    Designation,
    Fletter,
    Letter,
    Name,
    name,
    ProfilePic,
    Department,
    department,
    Qualification,
    HighestQualification,
    Linkedin,
    LinkedinProfileLink,
    Email,
    badgeLabel,
    onViewProfile,
  } = props;

  const staffName = Name || name || "Faculty Member";
  const staffRole = Role || Designation || "Staff";
  const staffDept = Department || department || "";
  const staffQual = Qualification || HighestQualification || "";
  const staffLinkedin = Linkedin || LinkedinProfileLink || "";

  const cleanName = (staffName || "")
    .replace(/^(Dr\.|Mr\.|Ms\.|Prof\.|Col\.)\s*/i, "")
    .trim();
  const initialLetter =
    Fletter || Letter || (cleanName ? cleanName[0].toUpperCase() : "F");

  // Determine category & style
  const roleCategory = classifyStaffRole(staffRole);

  let badgeText = badgeLabel || "FACULTY";
  let badgeStyle = "bg-slate-100 text-slate-700 border-slate-200";
  let accentBorder = "border-l-4 border-slate-400";
  let topGradient = "from-slate-600 to-slate-800";

  if (roleCategory === "dean") {
    badgeText = badgeLabel || "DEAN";
    badgeStyle = "bg-purple-100 text-purple-900 border-purple-300 font-bold";
    accentBorder = "border-l-4 border-purple-600";
    topGradient = "from-purple-600 to-indigo-600";
  } else if (roleCategory === "asst_director") {
    badgeText = badgeLabel || "ASST. DIRECTOR";
    badgeStyle = "bg-blue-100 text-blue-900 border-blue-300 font-bold";
    accentBorder = "border-l-4 border-blue-600";
    topGradient = "from-blue-600 to-cyan-600";
  } else if (roleCategory === "hod") {
    badgeText = badgeLabel || "HOD";
    badgeStyle = "bg-teal-100 text-teal-900 border-teal-300 font-bold";
    accentBorder = "border-l-4 border-[#32727a]";
    topGradient = "from-[#32727a] to-emerald-600";
  } else if (roleCategory === "staff" || badgeLabel) {
    badgeText = badgeLabel || "STAFF";
    badgeStyle = "bg-amber-50 text-amber-900 border-amber-200 font-bold";
    accentBorder = "border-l-4 border-amber-500";
    topGradient = "from-amber-500 to-orange-500";
  }

  const hasPhoto = isRealPhoto(ProfilePic);

  return (
    <div
      className={`group relative bg-white rounded-2xl border border-gray-200/80 ${accentBorder} shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden flex flex-col justify-between ${
        newClass ? newClass : "w-full p-5"
      }`}
    >
      {/* Top Accent Line */}
      <div
        className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${topGradient}`}
      />

      <div>
        {/* Category Tag */}
        <div className="flex justify-end mb-2">
          <span className="text-[10px] uppercase font-bold tracking-wider px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
            {badgeText}
          </span>
        </div>

        {/* Profile Avatar */}
        <div className="flex justify-center my-2">
          <div className="relative w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-md bg-gradient-to-tr from-[#081C3A] to-[#32727A] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform duration-300">
            {hasPhoto ? (
              <img
                src={ProfilePic}
                alt={staffName}
                className="w-full h-full object-cover object-top rounded-full"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <span className="text-white text-3xl font-bold font-serif uppercase tracking-wider">
                {initialLetter}
              </span>
            )}
          </div>
        </div>

        {/* Name */}
        <h3 className="text-gray-900 font-bold text-base md:text-lg text-center mt-2 leading-snug line-clamp-1 group-hover:text-[#32727a] transition-colors">
          {staffName}
        </h3>

        {/* Role Pill */}
        <div className="mt-2 flex justify-center">
          <span
            className={`inline-block text-xs px-3 py-1 rounded-full border text-center leading-snug max-w-[95%] truncate ${badgeStyle}`}
            title={staffRole}
          >
            {staffRole}
          </span>
        </div>

        {/* Department & Qualification Details */}
        <div className="mt-3 space-y-1.5 px-1">
          {staffDept && (
            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-600">
              <FaBuilding className="text-[#32727a] shrink-0 text-[11px]" />
              <span className="truncate max-w-[200px]" title={staffDept}>
                {staffDept}
              </span>
            </div>
          )}

          {staffQual && (
            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-700 font-medium">
              <FaGraduationCap className="text-[#32727a] shrink-0 text-[13px]" />
              <span className="bg-gray-100 text-gray-700 px-2.5 py-0.5 rounded-md text-[11px] font-semibold border border-gray-200">
                {staffQual}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Social / Email Links & View Profile Action */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex flex-col gap-2">
        {(staffLinkedin || Email) && (
          <div className="flex items-center justify-center gap-2">
            {staffLinkedin && (
              <a
                href={staffLinkedin}
                target="_blank"
                rel="noreferrer"
                title="LinkedIn Profile"
                className="inline-flex items-center gap-1.5 text-xs text-[#0A66C2] hover:text-white bg-blue-50 hover:bg-[#0A66C2] px-3 py-1 rounded-lg transition-all font-medium border border-blue-100 shadow-sm"
              >
                <FaLinkedin className="w-3.5 h-3.5" />
                <span>LinkedIn</span>
              </a>
            )}

            {Email && (
              <a
                href={`mailto:${Email}`}
                title="Send Email"
                className="inline-flex items-center gap-1.5 text-xs text-gray-700 hover:text-white bg-gray-100 hover:bg-gray-800 px-3 py-1 rounded-lg transition-all font-medium border border-gray-200 shadow-sm"
              >
                <FaEnvelope className="w-3 h-3" />
                <span>Email</span>
              </a>
            )}
          </div>
        )}

        {onViewProfile && (
          <button
            onClick={() => onViewProfile(props)}
            className="w-full py-1.5 px-3 rounded-xl bg-gray-50 hover:bg-[#081C3A] text-gray-700 hover:text-white text-xs font-bold transition-all duration-200 flex items-center justify-center gap-1.5 border border-gray-200 shadow-sm group/btn"
          >
            <span>View Profile</span>
            <span className="text-xs group-hover/btn:translate-x-1 transition-transform">
              &rarr;
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default FacultyCard;
